import { startAutoResize } from "./core/auto-resize.js";
import { BaseBridge } from "./core/base-bridge.js";
import { DEFAULT_PROTOCOL_VERSION } from "./core/constants.js";
import {
  NativeTransport,
  WindowTransport,
  detectNativeHost,
} from "./core/transport.js";
import type {
  Bridge,
  BridgeEnvironment,
  ContentBridgeConfig,
  Envelope,
  MessageEventLike,
  Transport,
  WindowLike,
} from "./core/types.js";

export type {
  Bridge,
  BridgeEnvironment,
  ContentBridgeConfig,
  Envelope,
  EnvelopeKind,
  FitPayload,
  MessageContext,
  MessageHandler,
  PostTarget,
  RequestOptions,
  ResizePayload,
  Transport,
} from "./core/types.js";
export {
  NativeTransport,
  WindowTransport,
  detectNativeHost,
  type NativePlatform,
} from "./core/transport.js";
export {
  CHANNEL,
  DEFAULT_PROTOCOL_VERSION,
  FIT_MESSAGE,
  NATIVE_CHANNEL,
  NATIVE_RECEIVE_GLOBAL,
  RESIZE_MESSAGE,
} from "./core/constants.js";
export { startAutoResize, type AutoResizeOptions } from "./core/auto-resize.js";

/** How often the content re-sends `connect` until the host answers. */
const CONNECT_RETRY_INTERVAL_MS = 100;
/** Give up connecting after this many attempts (~5s at the interval above). */
const MAX_CONNECT_ATTEMPTS = 50;

function globalWindow(): WindowLike | undefined {
  return (globalThis as unknown as { window?: WindowLike }).window;
}

/**
 * Auto-select the transport: a native WebView channel if one is detected on the
 * window (iOS/Android), otherwise the web `window.postMessage` transport.
 */
function autoContentTransport(self: WindowLike | undefined): Transport {
  if (!self) {
    throw new Error(
      "createContentBridge: no window available. Pass `{ window }` or `{ transport }`.",
    );
  }
  const native = detectNativeHost(self);
  return native ? new NativeTransport(self, native) : new WindowTransport(self);
}

/**
 * The untrusted content side of the bridge — works in a web iframe OR a native
 * WebView.
 *
 * - Web: posts `connect` to `window.parent` (`targetOrigin: "*"`), pins the parent
 *   origin on `init`, and validates source/origin on every inbound frame.
 * - Native: posts `connect` over the native channel; the native host is the sole
 *   trusted peer so origin/source validation is skipped. The same
 *   `connect`/`init`/`reject` handshake, `component_id`, `protocol_version` and
 *   request/response correlation apply unchanged.
 */
class ContentBridge extends BaseBridge {
  private readonly self: WindowLike | undefined;
  private connectAttempts = 0;
  private connectTimer: ReturnType<typeof setInterval> | null = null;
  private stopAutoResize: (() => void) | null = null;

  constructor(
    config: ContentBridgeConfig,
    transport: Transport,
    self: WindowLike | undefined,
  ) {
    super({
      protocolVersion: config.protocolVersion ?? DEFAULT_PROTOCOL_VERSION,
      componentId: null,
      transport,
      rolePrefix: "content",
    });
    this.self = self;
    this.startHandshake();
    // Report content size to the host (enables `fit`/auto sizing). Start only once
    // the channel is open: a failed/rejected handshake then never starts it (so the
    // outbox can't grow unbounded), and the peer is pinned before any `resize` is
    // posted. No-op without a DOM/ResizeObserver; torn down on destroy.
    if ((config.autoResize ?? true) && self) {
      this.onReady(() => {
        this.stopAutoResize = startAutoResize(
          this,
          self as unknown as Window & typeof globalThis,
        );
      });
    }
  }

  protected receive(envelope: Envelope, event: MessageEventLike): void {
    switch (envelope.kind) {
      case "init":
        this.handleInit(envelope, event);
        break;
      case "reject":
        this.handleReject(envelope, event);
        break;
      case "message":
      case "request":
      case "response":
      case "error":
        this.acceptOpenFrame(envelope, event);
        break;
      default:
        // `connect` is host-directed; the content never consumes it.
        break;
    }
  }

  protected isExpectedSource(source: unknown): boolean {
    const parent = this.self?.parent;
    return parent != null && source === parent;
  }

  protected isAllowedOrigin(origin: string): boolean {
    return this.peerOrigin !== null && origin === this.peerOrigin;
  }

  protected override emitReady(): void {
    this.stopConnectRetries();
    super.emitReady();
  }

  protected override failHandshake(error: Error): void {
    this.stopConnectRetries();
    super.failHandshake(error);
  }

  override destroy(): void {
    this.stopConnectRetries();
    this.stopAutoResize?.();
    this.stopAutoResize = null;
    super.destroy();
  }

  /** On native the sole peer is inherently trusted; on web verify the sender. */
  private isTrustedInbound(event: MessageEventLike): boolean {
    return this.transport.trustedPeer || this.isExpectedSource(event.source);
  }

  private startHandshake(): void {
    if (!this.transport.trustedPeer) {
      // Web: a real parent window is required. On native the content is the
      // top-level document (no window.parent), so this check is skipped.
      const parent = this.self?.parent;
      if (parent == null || parent === this.self) {
        this.failHandshake(
          new Error("createContentBridge: no parent window to connect to"),
        );
        return;
      }
    }
    this.postConnect();
    this.connectTimer = setInterval(() => {
      if (this.open || this.destroyed || this.handshakeError) {
        this.stopConnectRetries();
        return;
      }
      this.connectAttempts += 1;
      if (this.connectAttempts >= MAX_CONNECT_ATTEMPTS) {
        this.stopConnectRetries();
        this.failHandshake(
          new Error("createContentBridge: handshake timed out (no init from host)"),
        );
        return;
      }
      this.postConnect();
    }, CONNECT_RETRY_INTERVAL_MS);
    const maybe = this.connectTimer as unknown as { unref?: () => void };
    if (typeof maybe.unref === "function") maybe.unref();
  }

  private postConnect(): void {
    const connect = this.buildEnvelope({ kind: "connect" });
    if (this.transport.trustedPeer) {
      // Native: the transport delivers to the sole native host; target ignored.
      this.transport.post(connect, { window: null, targetOrigin: "*" });
      return;
    }
    const parent = this.self?.parent;
    if (parent == null) return;
    // The parent origin is unknown until `init`, so the initial connect uses "*".
    // It carries no secrets — only the channel discriminator and version.
    this.transport.post(connect, { window: parent, targetOrigin: "*" });
  }

  private stopConnectRetries(): void {
    if (this.connectTimer !== null) {
      clearInterval(this.connectTimer);
      this.connectTimer = null;
    }
  }

  private handleInit(envelope: Envelope, event: MessageEventLike): void {
    if (this.open || this.destroyed) return;
    if (!this.isTrustedInbound(event)) return;
    // The host already validated the version before sending `init`; double-check.
    if (envelope.protocol_version !== this.protocolVersion) return;
    if (!envelope.component_id) return;

    this.componentId = envelope.component_id;
    if (!this.transport.trustedPeer) {
      this.peerOrigin = event.origin; // pin the parent origin from here on (web)
      this.peerWindow = this.self?.parent ?? (event.source as WindowLike);
    }
    this.emitReady();
  }

  private handleReject(envelope: Envelope, event: MessageEventLike): void {
    if (this.open || this.destroyed) return;
    if (!this.isTrustedInbound(event)) return;
    this.failHandshake(
      new Error(envelope.error ?? "Handshake rejected by host"),
    );
  }
}

/**
 * Create the content side of the bridge and immediately begin the handshake.
 *
 * The transport is auto-selected: a native WebView channel (iOS/Android) if one is
 * detected on the window, otherwise the web `window.postMessage` transport. Pass
 * `environment.transport` to override (e.g. in tests).
 *
 * The IIFE build (`dist/sdk/v<major>/rc-sdk.js`) calls this automatically and
 * assigns the instance to `window.RC`; import this factory directly for advanced
 * or non-injected usage.
 *
 * @example
 * ```ts
 * const rc = createContentBridge({ protocolVersion: 1 });
 * rc.on("ping", (payload) => ({ pong: true, echo: payload }));
 * await rc.whenReady();
 * rc.send("content-ready", { at: Date.now() });
 * ```
 */
export function createContentBridge(
  config: ContentBridgeConfig = {},
  environment?: BridgeEnvironment,
): Bridge {
  const self = environment?.window ?? globalWindow();
  const transport = environment?.transport ?? autoContentTransport(self);
  return new ContentBridge(config, transport, self);
}
